import { useEffect, useRef } from "react";

export const WATCHLIST_POLL_INTERVAL_MS = 60_000;

/**
 * Poll while the page is visible and refresh immediately when the user returns.
 * Overlapping runs are skipped so a slow request cannot build up a request queue.
 */
export function useVisiblePolling(
  callback: () => void | Promise<void>,
  intervalMs = WATCHLIST_POLL_INTERVAL_MS,
) {
  const callbackRef = useRef(callback);
  const inFlightRef = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    let active = true;

    const run = () => {
      if (!active || document.visibilityState === "hidden" || inFlightRef.current) return;

      inFlightRef.current = true;
      void Promise.resolve()
        .then(() => callbackRef.current())
        .catch(() => undefined)
        .finally(() => {
          inFlightRef.current = false;
        });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") run();
    };

    const interval = window.setInterval(run, intervalMs);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", run);

    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", run);
    };
  }, [intervalMs]);
}
